import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import useAuthApi from "../api/useAuthApi";

const SignInLayer = () => {
  const { login } = useAuth();
  const { sendOtp, verifyOtp, loading } = useAuthApi();

  const [email, setEmail] = useState("");
  const [pin, setPin] = useState(new Array(6).fill(""));
  const [step, setStep] = useState(1);
  const [timer, setTimer] = useState(120);
  const inputRefs = useRef([]);

  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  const handlePinChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const resendOtp = async () => {
    setPin(new Array(6).fill(""));
    setTimer(120);
    inputRefs.current[0]?.focus();
    await sendOtp(email);
  };

  const formatTimer = () => {
    const m = String(Math.floor(timer / 60)).padStart(2, "0");
    const s = String(timer % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      alert("Please enter your email");
      return;
    }

    try {
      await sendOtp(email);
      setStep(2);
      setTimer(120);
      setPin(new Array(6).fill(""));
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleVerifySignIn = async (e) => {
    e.preventDefault();
    const isComplete = pin.every((digit) => digit !== "");
    if (!isComplete) {
      alert("Please enter all 6 digits");
      return;
    }

    try {
      const otp = pin.join("");
      const res = await verifyOtp(email, otp);
      login(res.data?.payload.token);
      localStorage.setItem('adminDetails', JSON.stringify(res.data?.payload.user))
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  const handleChangeEmail = () => {
    setStep(1);
    setEmail("");
    setPin(new Array(6).fill(""));
    setTimer(120);
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src="assets/images/auth/auth-img.png" alt="" />
        </div>
      </div>
      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <div>
            <h4 className="mb-12">Sign In to your Account</h4>
            <p className="mb-32 text-secondary-light text-lg">
              Welcome back! please enter your detail
            </p>
          </div>

          <form onSubmit={step === 1 ? handleSendOtp : handleVerifySignIn}>
            {step === 1 && (
              <>
                <div className="icon-field mb-20">
                  <span className="icon top-50 translate-middle-y">
                    <Icon icon="mage:email" />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-control h-56-px bg-neutral-50 radius-12"
                    placeholder="Email"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="d-flex justify-content-between gap-2 mb-20">
                  {pin.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handlePinChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      ref={(el) => (inputRefs.current[index] = el)}
                      className="form-control text-center h-56-px bg-neutral-50 radius-12"
                      style={{ width: "48px", flex: "1" }}
                    />
                  ))}
                </div>

                <div className="mb-12 text-center">
                  {timer > 0 ? (
                    <span className="text-secondary-light">
                      Resend OTP in {formatTimer()}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link text-primary text-sm"
                      onClick={resendOtp}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        "Resend OTP"
                      )}
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12"
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify & Sign In"}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;
